
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage, ElMessageBox } from "element-plus"
import { Card, CardRarityOption } from "@/store/modules/CardModule"
import { CardSeriesOption } from "@/store/modules/CardSeriesModule"
import { CardVersionOption } from "@/store/modules/CardVersionModule"
import { CardLanguageOption } from "@/store/modules/CardLanguageModule"

export default defineComponent({
  setup() {
    onMounted(() => {
      setCurrentPageBreadcrumbs("卡牌列表", ["抽卡系统"])
      loadOptions()
    })

    onUnmounted(() => {
      clearPollingInterval()
    })

    const store = useStore()
    const route = useRoute()
    const tableData = ref<Card[]>([])
    const cardSeriesOptions = ref<CardSeriesOption[]>([])
    const rarityOptions = ref<CardRarityOption[]>([])
    const versionOptions = ref<CardVersionOption[]>([])
    const languageOptions = ref<CardLanguageOption[]>([])

    const formData = ref({
      name: '',
      card_series_id: (route.query.card_series_id as string) || '',
      card_rarity_id: '',
      card_version_id: '',
      enabled: ''
    })

    const page = ref({
      currentPage: 1,
      pageSize: 10,
      totalResult: 0,
    })

    // ZIP upload refs
    const zipInputRef = ref<HTMLInputElement | null>(null)
    const uploadDialogVisible = ref(false)
    const progressDialogVisible = ref(false)
    const selectedZipFile = ref<File | null>(null)
    const zipUploading = ref(false)
    const uploadForm = ref({
      card_language_id: '' as number | string,
      card_version_id: '' as number | string,
      overwrite: false,
    })
    const uploadJobResult = ref<any>(null)
    const uploadStatus = ref<any>(null)
    let pollingTimer: ReturnType<typeof setInterval> | null = null

    const uploadProgressPercent = computed(() => {
      if (!uploadStatus.value) return 0
      const { done, total } = uploadStatus.value
      if (!total || total === 0) return 0
      return Math.min(100, Math.round((done / total) * 100))
    })

    const uploadProgressStatus = computed(() => {
      if (!uploadStatus.value) return ''
      const s = uploadStatus.value.status
      if (s === 'completed') return 'success'
      if (s === 'failed') return 'exception'
      return ''
    })

    const uploadStatusText = computed(() => {
      if (!uploadStatus.value) return '等待处理...'
      const s = uploadStatus.value.status
      if (s === 'processing') {
        const { done, total } = uploadStatus.value
        return `处理中（${done || 0} / ${total || 0}）`
      }
      if (s === 'completed') return '处理完成'
      if (s === 'failed') return '处理失败'
      return s || '等待处理...'
    })

    const isProgressComplete = computed(() => {
      if (!uploadStatus.value) return false
      const s = uploadStatus.value.status
      return s === 'completed' || s === 'failed'
    })

    const loadOptions = () => {
      // 加载系列选项
      store.dispatch(Actions.GET_ALL_CARD_SERIES).then(() => {
        cardSeriesOptions.value = store.getters.currentAllCardSeries
      })
      // 加载稀有度选项
      store.dispatch(Actions.GET_CARD_RARITIES).then(() => {
        rarityOptions.value = store.getters.currentCardRarities
      })
      // 加载版本选项
      store.dispatch(Actions.GET_ALL_CARD_VERSIONS).then(() => {
        versionOptions.value = store.getters.currentAllCardVersions
      })
      // 加载语系选项
      store.dispatch(Actions.GET_ALL_CARD_LANGUAGES).then(() => {
        languageOptions.value = store.getters.currentAllCardLanguages
      })
    }

    const query = () => {
      store
        .dispatch(Actions.GET_CARDS, { ...formData.value, ...page.value })
        .then(() => {
          tableData.value = store.getters.currentCardsList
          page.value.totalResult = store.getters.currentCardsCount
        })
        .catch(() => {
          ElMessage.error('获取卡牌列表失败')
        })
    }

    query()

    const submitEvent = () => {
      page.value.currentPage = 1
      query()
    }

    const onPageChange = (e: { currentPage: number; pageSize: number }) => {
      page.value.currentPage = e.currentPage
      page.value.pageSize = e.pageSize
      query()
    }

    const onReset = () => {
      formData.value = {
        name: '',
        card_series_id: '',
        card_rarity_id: '',
        card_version_id: '',
        enabled: ''
      }
      page.value.currentPage = 1
      query()
    }

    const getRarityTagType = (rarity: string) => {
      const typeMap: Record<string, string> = {
        'R': 'info',
        'SR': '',
        'SSR': 'success',
        'SSSR': 'warning',
        'UR': 'danger',
        'SAR': 'danger'
      }
      return typeMap[rarity] || ''
    }

    const onDelete = (row: Card) => {
      ElMessageBox.confirm(
        `确定要删除卡牌「${row.name}」吗？`,
        '删除确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(() => {
          store
            .dispatch(Actions.DELETE_CARD, row.id)
            .then((data: { success: boolean; error_msg?: string }) => {
              if (data.success) {
                ElMessage.success('删除成功')
                query()
              } else {
                ElMessage.error(data.error_msg || '删除失败')
              }
            })
            .catch(() => {
              ElMessage.error('删除失败')
            })
        })
        .catch(() => {
          // 取消删除
        })
    }

    const formatDate = (date: Date): string => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      const second = String(date.getSeconds()).padStart(2, '0')
      return `${year}${month}${day}${hour}${minute}${second}`
    }

    const onDownload = () => {
      store
        .dispatch(Actions.DOWNLOAD_CARDS, formData.value)
        .then(() => {
          const blob = new Blob([store.getters.currentDownloadCards], { type: "application/xlsx" })
          const link = document.createElement("a")
          link.href = URL.createObjectURL(blob)
          link.download = `cards_${formatDate(new Date())}.xlsx`
          link.click()
          URL.revokeObjectURL(link.href)
        })
        .catch(() => {
          ElMessage.error('下载失败')
        })
    }

    const onUpload = () => {
      const input = document.getElementById('btn_upload') as HTMLInputElement
      if (input) {
        input.click()
      }
    }

    const onFileChange = (e: Event) => {
      const target = e.target as HTMLInputElement
      const file = target.files?.[0]
      if (!file) return

      store
        .dispatch(Actions.UPLOAD_CARDS, { file })
        .then((data: { success: boolean; error_msg?: string; imported_count?: number }) => {
          if (data.success) {
            ElMessage.success(`上传成功，共导入 ${data.imported_count || 0} 条记录`)
            query()
          } else {
            ElMessage.error(data.error_msg || '上传失败')
          }
        })
        .catch(() => {
          ElMessage.error('上传失败')
        })
        .finally(() => {
          // 清空文件选择，允许重复选择同一文件
          target.value = ''
        })
    }

    // ZIP upload methods

    const onOpenZipUploadDialog = () => {
      uploadForm.value = {
        card_language_id: '',
        card_version_id: '',
        overwrite: false,
      }
      selectedZipFile.value = null
      uploadJobResult.value = null
      uploadStatus.value = null
      uploadDialogVisible.value = true
    }

    const onUploadDialogClosed = () => {
      selectedZipFile.value = null
      if (zipInputRef.value) {
        zipInputRef.value.value = ''
      }
    }

    const onSelectZip = () => {
      zipInputRef.value?.click()
    }

    const onZipFileChange = (e: Event) => {
      const target = e.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        selectedZipFile.value = file
      }
    }

    const clearPollingInterval = () => {
      if (pollingTimer !== null) {
        clearInterval(pollingTimer)
        pollingTimer = null
      }
    }

    const MAX_POLL_ATTEMPTS = 300 // 10 分钟（每 2s 一次）
    let pollAttempts = 0

    const startPolling = (jobId: string) => {
      clearPollingInterval()
      pollAttempts = 0
      pollingTimer = setInterval(() => {
        pollAttempts++
        if (pollAttempts > MAX_POLL_ATTEMPTS) {
          clearPollingInterval()
          uploadStatus.value = { status: 'failed', error_message: '轮询超时，请刷新页面确认结果', total: 0, done: 0, results: [] }
          return
        }
        store
          .dispatch(Actions.GET_CARD_IMAGES_UPLOAD_STATUS, jobId)
          .then((data: any) => {
            uploadStatus.value = data
            if (data.status === 'completed' || data.status === 'failed') {
              clearPollingInterval()
              if (data.status === 'completed') {
                query()
              }
            }
          })
          .catch(() => {
            // 忽略单次轮询网络错误，继续重试
          })
      }, 2000)
    }

    const onStartUpload = () => {
      if (!selectedZipFile.value) return
      zipUploading.value = true

      const params: {
        file: File;
        card_language_id?: number | string;
        card_version_id?: number | string;
        overwrite: boolean;
      } = {
        file: selectedZipFile.value,
        overwrite: uploadForm.value.overwrite,
      }
      if (uploadForm.value.card_language_id !== '') {
        params.card_language_id = uploadForm.value.card_language_id
      }
      if (uploadForm.value.card_version_id !== '') {
        params.card_version_id = uploadForm.value.card_version_id
      }

      store
        .dispatch(Actions.UPLOAD_CARD_IMAGES, params)
        .then((data: any) => {
          uploadJobResult.value = data
          uploadDialogVisible.value = false
          progressDialogVisible.value = true
          if (data.job_id && data.process_count > 0) {
            uploadStatus.value = null
            startPolling(data.job_id)
          } else {
            // 没有需要处理的图片（全部跳过或空 ZIP）—— 直接标记完成，避免关闭按钮卡死
            uploadStatus.value = { status: 'completed', total: 0, done: 0, results: [] }
          }
        })
        .catch((err: any) => {
          ElMessage.error(err?.error_msg || err?.message || '上传失败')
        })
        .finally(() => {
          zipUploading.value = false
          if (zipInputRef.value) {
            zipInputRef.value.value = ''
          }
        })
    }

    const onCloseProgress = () => {
      clearPollingInterval()
      progressDialogVisible.value = false
      uploadJobResult.value = null
      uploadStatus.value = null
    }

    return {
      tableData,
      formData,
      cardSeriesOptions,
      rarityOptions,
      versionOptions,
      languageOptions,
      submitEvent,
      page,
      onPageChange,
      onReset,
      getRarityTagType,
      onDelete,
      onDownload,
      onUpload,
      onFileChange,
      // ZIP upload
      zipInputRef,
      uploadDialogVisible,
      progressDialogVisible,
      selectedZipFile,
      zipUploading,
      uploadForm,
      uploadJobResult,
      uploadStatus,
      uploadProgressPercent,
      uploadProgressStatus,
      uploadStatusText,
      isProgressComplete,
      onOpenZipUploadDialog,
      onUploadDialogClosed,
      onSelectZip,
      onZipFileChange,
      onStartUpload,
      onCloseProgress,
    }
  },
})
